import React from 'react';
import { Artist, UserRole, Project, canEditArtists, canDeleteArtists } from '../types';
import { ICONS } from '../constants';
import { getAvatarColor, getInitials, getAvatarUrl } from '../utils/avatarUtils';
import { getProjectBadgeColor } from '../utils/projectUtils';
import { formatCardDateRange } from '../utils/dateUtils';
import { useTranslation } from '../hooks/useTranslation';
import Button from './ui/Button';

interface ArtistRowProps {
  artist: Artist;
  projects: Project[];
  currentRole: string | null;
  onEdit: (artist: Artist) => void;
  onDelete: (artistId: string) => void;
  isSelected: boolean;
  onSelect: (event: React.MouseEvent) => void;
}

const getAvailabilityStatus = (availability: string[]): { status: string; className: string } => {
  if (availability.length === 0) return { status: 'Нет данных', className: 'text-gray-500' };
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayStr = today.toISOString().split('T')[0];

  // Filter and normalize dates to YYYY-MM-DD format
  const validDates = availability
    .map(dateStr => {
      // Handle various date formats
      if (typeof dateStr !== 'string') return null;
      
      // If it's already in YYYY-MM-DD format
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        return dateStr;
      }
      
      // If it's ISO format with time
      if (dateStr.includes('T') || dateStr.includes('Z')) {
        const date = new Date(dateStr);
        if (!isNaN(date.getTime())) {
          return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        }
      }
      
      return null;
    })
    .filter((date): date is string => date !== null);

  if (validDates.length === 0) return { status: 'Нет данных', className: 'text-gray-500' };

  const sortedDates = validDates.sort();
  
  // Найти самую раннюю и самую позднюю даты
  const earliestDate = sortedDates[0];
  const latestDate = sortedDates[sortedDates.length - 1];
  
  if (!earliestDate) return { status: 'Нет данных', className: 'text-gray-500' };

  // Если самая поздняя дата < сегодня, информация устарела
  if (latestDate < todayStr) {
    return { status: 'Уточнить', className: 'text-orange-500' };
  }

  // Если самая ранняя дата <= сегодня <= самая поздняя дата, доступен сейчас
  if (earliestDate <= todayStr && todayStr <= latestDate) {
    // Показываем диапазон для строкового вида (более компактно)
    if (sortedDates.length > 1 && earliestDate !== latestDate) {
      const startDate = new Date(earliestDate + 'T00:00:00');
      const endDate = new Date(latestDate + 'T00:00:00');
      return { 
        status: `Доступен (до ${endDate.toLocaleDateString('ru-RU', {day: '2-digit', month: 'short'})})`, 
        className: 'text-green-500' 
      };
    }
    return { status: 'Доступен', className: 'text-green-500' };
  }

  // Если самая ранняя дата > сегодня, будет доступен с этой даты
  if (earliestDate > todayStr) {
    const startDate = new Date(earliestDate + 'T00:00:00');
    if (sortedDates.length > 1 && earliestDate !== latestDate) {
      const endDate = new Date(latestDate + 'T00:00:00');
      return { 
        status: `С ${startDate.toLocaleDateString('ru-RU', {day: '2-digit', month: 'short'})} - ${endDate.toLocaleDateString('ru-RU', {day: '2-digit', month: 'short'})}`, 
        className: 'text-yellow-500' 
      };
    }
    return { status: `С ${startDate.toLocaleDateString('ru-RU', {day: '2-digit', month: 'short'})}`, className: 'text-yellow-500' };
  }

  return { status: 'Нет данных', className: 'text-gray-500' };
};


// Helper: Check if project is active (current date is within project date range)
const isProjectActive = (project: Project): boolean => {
  try {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const startDate = new Date(project.startDate);
    startDate.setHours(0, 0, 0, 0);
    
    const endDate = new Date(project.endDate);
    endDate.setHours(23, 59, 59, 999);
    
    return today >= startDate && today <= endDate;
  } catch {
    return false;
  }
};

const ArtistRow: React.FC<ArtistRowProps> = ({ artist, projects, currentRole, onEdit, onDelete, isSelected, onSelect }) => {
  const { t } = useTranslation();
  const [avatarLoading, setAvatarLoading] = React.useState(false);
  const [avatarError, setAvatarError] = React.useState(false);
  const [clickTimeout, setClickTimeout] = React.useState<NodeJS.Timeout | null>(null);
  const availability = getAvailabilityStatus(artist.availability);
  
  // Only show active projects (current date within project date range)
  const assignedProjects = artist.assignedProjects
    .map(pId => projects.find(p => p.id === pId))
    .filter(Boolean) as Project[];
  const activeProjects = assignedProjects.filter(isProjectActive);

  // Double-click handler for editing
  const handleRowClick = (event: React.MouseEvent) => {
    if (clickTimeout) {
      // Double click detected
      clearTimeout(clickTimeout);
      setClickTimeout(null);
      
      if (canEditArtists(currentRole)) {
        onEdit(artist);
      }
    } else {
      // Single click - set timeout
      const timeout = setTimeout(() => {
        onSelect(event);
        setClickTimeout(null);
      }, 300);
      setClickTimeout(timeout);
    }
  };

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (clickTimeout) {
        clearTimeout(clickTimeout);
      }
    };
  }, [clickTimeout]);

  // Avatar state management
  React.useEffect(() => {
    if (artist.avatarUrl) {
      setAvatarLoading(true);
      setAvatarError(false);
    } else {
      setAvatarLoading(false);
      setAvatarError(false);
    }
  }, [artist.avatarUrl]);

  const rowClasses = `
    bg-gray-800 rounded-lg p-3 flex items-center justify-between 
    transition-all hover:bg-gray-700/50 cursor-pointer
    ${isSelected ? 'bg-indigo-900/50 ring-1 ring-indigo-600' : ''}
  `;

  return (
    <div className={rowClasses} onClick={handleRowClick} data-artist-id={artist.id}>
      <div className="flex items-center gap-4 flex-1 min-w-0">
        {(() => {
          const avatarUrl = getAvatarUrl(artist.avatarUrl);
          return avatarUrl && !avatarError ? (
            <div className="relative w-12 h-12 flex-shrink-0">
              {avatarLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-700 rounded-full">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-500"></div>
                </div>
              )}
              <img 
                src={avatarUrl}
                alt={artist.name} 
                className={`w-12 h-12 rounded-full object-cover pointer-events-none transition-opacity duration-200 ${avatarLoading ? 'opacity-0' : 'opacity-100'}`}
                onError={(e) => {
                  console.log('❌ [ArtistRow] Avatar failed to load:', avatarUrl);
                  setAvatarError(true);
                  setAvatarLoading(false);
                }}
                onLoad={() => {
                  console.log('✅ [ArtistRow] Avatar loaded successfully:', avatarUrl);
                  setAvatarLoading(false);
                  setAvatarError(false);
                }}
              />
            </div>
          ) : (
            <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${getAvatarColor(artist.name)} flex items-center justify-center flex-shrink-0 pointer-events-none`}>
              <span className="text-white text-sm font-bold">
                <span className="avatar-text">{getInitials(artist.name)}</span>
              </span>
            </div>
          );
        })()}
        <div className="min-w-0">
          <p className="text-lg font-bold text-white truncate">{artist.name}</p>
          {artist.registrationStatus === 'pending' && (
            <span className="text-xs font-semibold text-yellow-400">{t('profile.access.pending')}</span>
          )}
          {artist.location && (
            <p className="text-xs text-gray-300 truncate mt-1 flex items-center">
              <ICONS.MapPin className="w-3 h-3 mr-1 flex-shrink-0" />
              {artist.location}
            </p>
          )}
           {artist.description && (
            <p className="text-xs text-gray-400 truncate mt-1">{artist.description}</p>
          )}
        </div>
      </div>
      
      <div className="hidden lg:flex items-center gap-2 mx-4 flex-shrink-0">
        {activeProjects.length > 0 ? (
          <>
            {activeProjects.slice(0, 2).map(p => (
              <div key={p.id} className={`px-2 py-1 ${getProjectBadgeColor(p.id)} text-xs font-bold text-white rounded-full text-center whitespace-nowrap`}>
                {p.name} <span className="font-normal opacity-75">{formatCardDateRange(p.startDate, p.endDate)}</span>
              </div>
            ))}
            {activeProjects.length > 2 && (
              <span className="text-xs text-gray-400">+{activeProjects.length - 2}</span>
            )}
          </>
        ) : (
          <span className="text-xs text-gray-500 italic">{t('artistCard.noActiveProjects')}</span>
        )}
      </div>

      <div className="hidden md:flex items-center gap-6 mx-4">
        <div className="flex items-center gap-2">
          {artist.software.slice(0, 4).map(s => (
            <span key={s} className="px-2 py-1 bg-gray-700 text-xs font-medium text-gray-300 rounded-full">{s}</span>
          ))}
          {artist.software.length > 4 && (
            <span className="text-xs text-gray-400">+{artist.software.length - 4}</span>
          )}
        </div>
      </div>

      <div className="hidden sm:block mx-4 text-center">
         <p className={`text-sm font-semibold whitespace-nowrap ${availability.className}`}>{availability.status}</p>
      </div>
      
      <div className="flex items-center gap-2">
        {artist.portfolioUrl && (
          <a href={artist.portfolioUrl} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-indigo-400 p-2 rounded-full hover:bg-gray-700" onClick={(e) => e.stopPropagation()}>
            <ICONS.Link className="w-5 h-5" />
          </a>
        )}
        {canEditArtists(currentRole) && (
          <span className="text-xs text-gray-400 italic hidden sm:block">{t('artistCard.doubleClickToEdit')}</span>
        )}
        {canDeleteArtists(currentRole) && (
          <Button variant="danger" onClick={(e) => { e.stopPropagation(); onDelete(artist.id); }} className="!p-2">
            <ICONS.Trash className="w-4 h-4" />
          </Button>
        )}
      </div>
    </div>
  );
};

export default ArtistRow;